import type { ReactNode } from 'react';
import type { ProjectContractor, ProjectReader } from '@/api/client';
import { BudgetRoundCheckbox } from '@/components/ui/BudgetRoundCheckbox/BudgetRoundCheckbox';
import {
  getContractorTableCells,
  getReaderTableCells,
  type ReaderTablePriceVariant,
} from '@/utils/formatNumber';

import './ReaderContractorCellTables.css';
import { highlightPlainText } from '@/utils/highlightSearchTerms';

export interface ContractorsDataTableProps {
  contractors: ProjectContractor[];
  /** Если задан — чекбокс бюджета кликабелен и вызывает переключение (ключ строки project_contractors, не только contractor_id) */
  onBudgetToggle?: (rowKey: string, nextIncluded: boolean) => void;
  /** Блокировка на время сохранения */
  budgetSaving?: boolean;
  /** Компактный режим: без таблицы, только имена, выравнивание влево */
  compact?: boolean;
  /** Продолжение под первыми строками — без дубля шапки */
  omitTableHeader?: boolean;
  searchHighlightWords?: string[];
  hidePrices?: boolean;
}

function hl(text: string, words: string[] | undefined): ReactNode {
  if (!words?.length) return text;
  return highlightPlainText(text, words);
}

function cellOrNa(value: string, words?: string[]): ReactNode {
  return value.trim() ? hl(value, words) : <span className="projects-table__empty-value">N/A</span>;
}

function cellReaderPrice(price: string, variant: ReaderTablePriceVariant, words?: string[], priceFixed?: boolean): ReactNode {
  if (variant === 'amount') {
    const t = price.trim();
    const amount = t ? hl(t, words) : <span className="projects-table__empty-value">N/A</span>;
    if (!priceFixed) return amount;
    return (
      <>
        <span className="projects-table__empty-value">ФИКС</span>
        {' '}
        {amount}
      </>
    );
  }
  if (variant === 'not_selected') return hl('Не выбрано', undefined);
  return <span className="projects-table__empty-value">N/A</span>;
}

export function ReadersDataTable({
  readers,
  compact,
  omitTableHeader,
  searchHighlightWords,
  hidePrices = false,
}: {
  readers: ProjectReader[];
  /** Компактный режим: без таблицы, только имена, выравнивание влево */
  compact?: boolean;
  /** Продолжение под первыми строками — без дубля шапки мини-таблицы */
  omitTableHeader?: boolean;
  searchHighlightWords?: string[];
  hidePrices?: boolean;
}) {
  if (compact) {
    return (
      <div className="projects-table__compact-names projects-table__compact-names--readers">
        {readers.map((r, idx) => (
          <span key={`${r.id}-${idx}`} className="projects-table__compact-name-line">
            {r.name?.trim() ? hl(r.name, searchHighlightWords) : <span className="projects-table__empty-value">N/A</span>}
          </span>
        ))}
      </div>
    );
  }

  return (
    <table className="projects-table__mini-data-table projects-table__mini-data-table--readers">
      <colgroup>
        <col className="projects-table__mini-data-table__col projects-table__mini-data-table__col--readers-name" />
        {!hidePrices ? (
          <col className="projects-table__mini-data-table__col projects-table__mini-data-table__col--readers-price" />
        ) : null}
        <col className="projects-table__mini-data-table__col projects-table__mini-data-table__col--readers-corrections" />
        <col className="projects-table__mini-data-table__col projects-table__mini-data-table__col--readers-time" />
        <col className="projects-table__mini-data-table__col projects-table__mini-data-table__col--readers-coef" />
      </colgroup>
      {!omitTableHeader ? (
        <thead>
          <tr>
            <th className="projects-table__mini-data-table__th" scope="col">
              Чтец
            </th>
            {!hidePrices ? (
              <th className="projects-table__mini-data-table__th projects-table__mini-data-table__th_num" scope="col">
                Стоимость
              </th>
            ) : null}
            <th className="projects-table__mini-data-table__th projects-table__mini-data-table__th_num" scope="col">
              Правки
            </th>
            <th
              className="projects-table__mini-data-table__th projects-table__mini-data-table__th_time projects-table__mini-data-table__cell--readers-after-corrections-spacing"
              scope="col"
            >
              Время
            </th>
            <th className="projects-table__mini-data-table__th projects-table__mini-data-table__th_num" scope="col">
              КФ
            </th>
          </tr>
        </thead>
      ) : null}
      <tbody>
        {readers.map((r, idx) => {
          const row = getReaderTableCells(r);
          return (
            <tr key={`${r.id}-${idx}`}>
              <td className="projects-table__mini-data-table__td">{hl(row.name, searchHighlightWords)}</td>
              {!hidePrices ? (
                <td className="projects-table__mini-data-table__td projects-table__mini-data-table__td_num">
                  {cellReaderPrice(row.price, row.priceVariant, searchHighlightWords, r.is_reader_price_fixed === true)}
                </td>
              ) : null}
              <td className="projects-table__mini-data-table__td projects-table__mini-data-table__td_num">
                {cellOrNa(row.corrections, searchHighlightWords)}
              </td>
              <td className="projects-table__mini-data-table__td projects-table__mini-data-table__td_time projects-table__mini-data-table__cell--readers-after-corrections-spacing">
                {cellOrNa(row.time, searchHighlightWords)}
              </td>
              <td className="projects-table__mini-data-table__td projects-table__mini-data-table__td_num">
                {cellOrNa(row.coef, searchHighlightWords)}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export function ContractorsDataTable({
  contractors,
  onBudgetToggle,
  budgetSaving,
  compact,
  omitTableHeader,
  searchHighlightWords,
  hidePrices = false,
}: ContractorsDataTableProps) {
  if (compact) {
    return (
      <div className="projects-table__compact-names projects-table__compact-names--contractors">
        {contractors.map((c) => {
          const row = getContractorTableCells(c);
          const inBudget = c.is_included_in_budget === true || c.is_contractor_included_in_budget === true;
          return (
            <span
              key={c.project_contract_id ?? c.legal_entity_id ?? c.id}
              className={
                inBudget
                  ? 'projects-table__compact-name-line projects-table__compact-name-line--contractor-in-budget'
                  : 'projects-table__compact-name-line'
              }
            >
              {row.name.trim() ? hl(row.name, searchHighlightWords) : <span className="projects-table__empty-value">N/A</span>}
            </span>
          );
        })}
      </div>
    );
  }

  return (
    <table className="projects-table__mini-data-table projects-table__mini-data-table--contractors">
      <colgroup>
        <col className="projects-table__mini-data-table__col projects-table__mini-data-table__col--contractors-name" />
        <col className="projects-table__mini-data-table__col projects-table__mini-data-table__col--contractors-service" />
        {!hidePrices ? (
          <col className="projects-table__mini-data-table__col projects-table__mini-data-table__col--contractors-price" />
        ) : null}
      </colgroup>
      {!omitTableHeader ? (
        <thead>
          <tr>
            <th className="projects-table__mini-data-table__th" scope="col">
              Контрагент
            </th>
            <th className="projects-table__mini-data-table__th" scope="col">
              Договор
            </th>
            {!hidePrices ? (
              <th className="projects-table__mini-data-table__th projects-table__mini-data-table__th_num" scope="col">
                Стоимость
              </th>
            ) : null}
          </tr>
        </thead>
      ) : null}
      <tbody>
        {contractors.map((c) => {
          const row = getContractorTableCells(c);
          const inBudget = c.is_included_in_budget === true || c.is_contractor_included_in_budget === true;
          const pcId = c.project_contract_id ?? c.legal_entity_id ?? c.id;
          return (
            <tr
              key={pcId}
              className={
                inBudget ? 'projects-table__mini-data-table__tr projects-table__mini-data-table__tr_contractor-in-budget' : undefined
              }
            >
              <td className="projects-table__mini-data-table__td projects-table__mini-data-table__td_contractor-name">
                <span className="projects-table__mini-data-table__contractor-name-inner">
                  {!hidePrices ? (
                    <BudgetRoundCheckbox
                      checked={inBudget}
                      interactive={!!onBudgetToggle}
                      onChange={
                        onBudgetToggle ? (next) => onBudgetToggle(pcId, next) : undefined
                      }
                      disabled={budgetSaving}
                    />
                  ) : null}
                  <span className="projects-table__mini-data-table__contractor-name-text">
                    {hl(row.name, searchHighlightWords)}
                  </span>
                </span>
              </td>
              <td className="projects-table__mini-data-table__td projects-table__mini-data-table__td_contractor-service">
                {cellOrNa(row.service, searchHighlightWords)}
              </td>
              {!hidePrices ? (
                <td className="projects-table__mini-data-table__td projects-table__mini-data-table__td_num projects-table__mini-data-table__td_contractor-price">
                  {cellOrNa(row.price, searchHighlightWords)}
                </td>
              ) : null}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
